import {displayedOrigin} from "nav/displayedOrigin";
import {isWellFormedReferralCode, normalizeReferralCode, REFERRAL_QUERY_PARAM} from "./referralAttribution";

/** The current site's share link. */
export type ReferralShareLink = {
	url: string;
	/**
	 * The host `url` actually points at, e.g. `dev.transferify.ro`.
	 *
	 * The row's own label, taken FROM the link rather than from a catalog wordmark (ui#1517). The
	 * catalog carried "transferify.cloud"/"transferify.ro" as fixed strings, which was true only in
	 * production: once the links follow the browser's environment, a fixed wordmark labels a dev link
	 * with a production address — an advocate would copy one address believing they had copied another.
	 */
	host: string;
};

/**
 * The referral card's link on the browser's current origin (ui#1517).
 *
 * A displayed link never enumerates known brands. The site being viewed is the site being shared, so
 * adding another brand needs no referral-card change and one site never advertises another domain.
 * Before the browser origin is known, or when the code is unusable, there is no link to copy.
 */
export function referralShareLink(code: string, currentOrigin: string | null | undefined): ReferralShareLink | null {
	const normalized = normalizeReferralCode(code);
	if (!isWellFormedReferralCode(normalized)) return null;
	const origin = displayedOrigin(currentOrigin);
	return origin === null ? null : linkOn(origin, normalized);
}

/** One origin's share URL and the host it names, so the two can never disagree. */
function linkOn(origin: string, code: string): {url: string; host: string} {
	return {url: referralShareLinkFor(origin, code), host: new URL(origin).host};
}

/** The share link for one origin, e.g. `<origin>/?ref=K7QN4RTX`. */
export function referralShareLinkFor(origin: string, code: string): string {
	const base = origin.endsWith("/") ? origin.slice(0, -1) : origin;
	return `${base}/?${REFERRAL_QUERY_PARAM}=${normalizeReferralCode(code)}`;
}
